import { useState } from "react";
import { Columns3, Save, X } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

const slug = (l) => l.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, "-");
const MODELES_KEY = "effectif-export-modeles";
const loadModeles = () => {
  try { const m = JSON.parse(localStorage.getItem(MODELES_KEY)); return Array.isArray(m) ? m : []; } catch { return []; }
};
const same = (a, b) => a.length === b.length && a.every((x) => b.includes(x));

const Chip = ({ name, cols, sansFiche = false, selected, actifFiltre, onApply, onDelete }) => (
  <span className={`inline-flex items-center rounded-full border text-xs font-semibold transition-colors ${same(cols, selected) && sansFiche === actifFiltre ? "border-lime bg-lime text-ink" : "border-line text-chalk hover:border-lime"}`}>
    <button type="button" data-testid={`export-modele-${slug(name)}`} onClick={() => onApply(cols, sansFiche)} className="px-2.5 py-1">{name}</button>
    {onDelete && <button type="button" data-testid={`export-modele-delete-${slug(name)}`} onClick={onDelete} aria-label={`Supprimer le modèle ${name}`} className="pr-2 opacity-70 hover:opacity-100"><X className="h-3 w-3" /></button>}
  </span>
);

const SaveModele = ({ onSave }) => {
  const [name, setName] = useState("");
  const save = () => { if (onSave(name.trim())) setName(""); };
  return (
    <div className="mt-2 flex gap-1">
      <input data-testid="export-modele-name" value={name} maxLength={30} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} placeholder="Nom du modèle" className="min-w-0 flex-1 rounded-lg border border-line bg-ink px-2 py-1 text-sm text-chalk outline-none focus:border-lime" />
      <button type="button" data-testid="export-modele-save" onClick={save} aria-label="Enregistrer la sélection" title="Enregistrer la sélection" className="rounded-lg bg-lime px-2 text-ink"><Save className="h-4 w-4" /></button>
    </div>
  );
};

export const ExportColumnsPicker = ({ labels, presets, selected, sansFiche, onChange, className }) => {
  const [modeles, setModeles] = useState(loadModeles);
  const persist = (m) => { setModeles(m); localStorage.setItem(MODELES_KEY, JSON.stringify(m)); };
  const toggle = (l) => onChange(selected.includes(l) ? selected.filter((x) => x !== l) : labels.filter((x) => x === l || selected.includes(x)), sansFiche);
  const apply = (cols, filtre = false) => onChange(labels.filter((l) => cols.includes(l)), filtre);
  const save = (name) => {
    if (!name) { toast.error("Donne un nom au modèle"); return false; }
    if (selected.length === 0) { toast.error("Coche au moins une colonne"); return false; }
    persist([...modeles.filter((m) => m.name !== name), { name, cols: selected, sansFiche }]);
    toast.success(`Modèle « ${name} » enregistré`);
    return true;
  };
  const link = "text-xs font-semibold text-lime hover:underline";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button data-testid="export-columns-button" className={className}><Columns3 className="h-4 w-4" />Colonnes ({selected.length}/{labels.length})</button>
      </PopoverTrigger>
      <PopoverContent data-testid="export-columns-panel" align="end" className="w-72 border-line bg-pitch p-3 text-chalk">
        <p className="font-mono text-[11px] uppercase tracking-[0.15em] text-dim">Modèles</p>
        <div data-testid="export-modeles" className="mt-2 flex flex-wrap gap-1.5">
          {presets.map((p) => <Chip key={p.name} {...p} selected={selected} actifFiltre={sansFiche} onApply={apply} />)}
          {modeles.map((m) => <Chip key={`u-${m.name}`} {...m} selected={selected} actifFiltre={sansFiche} onApply={apply} onDelete={() => persist(modeles.filter((x) => x.name !== m.name))} />)}
        </div>
        <label className="mt-2 flex cursor-pointer items-center gap-2 text-sm text-chalk">
          <input type="checkbox" data-testid="export-sans-fiche" checked={sansFiche} onChange={(e) => onChange(selected, e.target.checked)} className="h-4 w-4 accent-[#D4AF37]" />
          Seulement les enfants sans fiche
        </label>
        <SaveModele onSave={save} />
        <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.15em] text-dim">Colonnes à exporter</p>
        <div className="mt-2 flex gap-3">
          <button data-testid="export-columns-all" onClick={() => onChange(labels, sansFiche)} className={link}>Tout cocher</button>
          <button data-testid="export-columns-none" onClick={() => onChange([], sansFiche)} className={link}>Tout décocher</button>
        </div>
        <div className="mt-2 max-h-60 space-y-1 overflow-y-auto pr-1">
          {labels.map((l) => (
            <label key={l} className="flex cursor-pointer items-center gap-2 rounded px-1 py-1 text-sm hover:bg-white/5">
              <input type="checkbox" data-testid={`export-col-${slug(l)}`} checked={selected.includes(l)} onChange={() => toggle(l)} className="h-4 w-4 accent-[#D4AF37]" />
              {l}
            </label>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};
